import type { CreateNewsDto, ListDto } from "@/entities/types/entities";
import type { PostLocaleCopy } from "@/entities/types/editor";
import { getPostBodyHtml, getPostExcerpt, getPostOutline } from "@/shared/utils/postEditor";

export type PostCopy = {
    title: string;
    subtitle: string;
    bodyHtml: string;
    keyMessage: string;
    quote: string;
    outlineTitle: string;
    outlineItems: string[];
    /** Outline blocks for the detail page (EN replaces the first block when filled). */
    outline: ListDto[];
};

const isEnglish = (lang?: string) => !!lang && lang.toLowerCase().startsWith("en");

const clean = (value?: string) => (typeof value === "string" ? value.trim() : "");

const cleanItems = (items?: string[]) => (items ?? []).map((item) => clean(item)).filter(Boolean);

export const getPostEnglishCopy = (post?: CreateNewsDto): PostLocaleCopy | undefined => post?.editor?.locales?.en;

/** Russian (primary) copy — the backward-compatible root fields. */
const resolveRuCopy = (post?: CreateNewsDto): PostCopy => {
    const outline = getPostOutline(post);
    const first = outline[0];

    return {
        title: post?.editor?.title || post?.title || "",
        subtitle: getPostExcerpt(post),
        bodyHtml: getPostBodyHtml(post),
        keyMessage: post?.editor?.keyMessage || post?.contentItems?.[1]?.text || "",
        quote: post?.editor?.quote || post?.contentItems?.[2]?.text || "",
        outlineTitle: first?.title || "",
        outlineItems: first?.items ?? [],
        outline,
    };
};

/**
 * Resolves localized post copy. For `en` returns EN fields from `editor.locales.en`,
 * falling back per-field to RU when EN is empty.
 */
export const resolvePostCopy = (post: CreateNewsDto | undefined, lang?: string): PostCopy => {
    const ru = resolveRuCopy(post);
    if (!isEnglish(lang)) return ru;

    const en = getPostEnglishCopy(post);
    if (!en) return ru;

    const enItems = cleanItems(en.outlineItems);
    const enOutlineTitle = clean(en.outlineTitle);
    const hasEnOutline = enItems.length > 0;

    const outlineTitle = hasEnOutline ? enOutlineTitle || ru.outlineTitle : ru.outlineTitle;
    const outlineItems = hasEnOutline ? enItems : ru.outlineItems;

    const outline: ListDto[] = hasEnOutline
        ? [{ title: outlineTitle, items: outlineItems }, ...ru.outline.slice(1)]
        : ru.outline;

    return {
        title: clean(en.title) || ru.title,
        subtitle: clean(en.subtitle) || ru.subtitle,
        bodyHtml: clean(en.bodyHtml) ? (en.bodyHtml as string) : ru.bodyHtml,
        keyMessage: clean(en.keyMessage) || ru.keyMessage,
        quote: clean(en.quote) || ru.quote,
        outlineTitle,
        outlineItems,
        outline,
    };
};
